<template>
<div class="chapter">
<div class="inner_page">
<h1 class="western4" id="calibre_toc_3">Chapter One</h1>
<h1 class="western7">All the Towns’ Folk</h1>
<p class="c4"> <br class="calibre1"/></p>
<p class="c4">Dejected by the dead world beneath him, Cahz closed
his eyes and listened to the throb of the helicopter’s engine. The
constant heavy beating of the blades and the drone of the turbine
encased him in noise. He cradled the solid metal body of his
carbine, its hard surface reassuring to his touch. The weapon sat
in an unorthodox position, butt hooked over his forearm with the
muzzle pointing at his feet. With every rock or turn the chopper
made, the stock jostled against the helmet sitting in his lap,
making a low clunking noise.</p>
<p class="western5">It was a sound almost lost to the din of the
cabin, but Cahz felt it and loathed it, like the incessant dripping
of a leaky tap, coercing his silent rage to a crescendo. With his
eyes closed he focused on his physical state. His backside was
numb, his legs were aching for a stretch, and there was a sharp
pain in his shoulder from a strap on his body armour that was
biting into his flesh. But it was the most comfortable arrangement
he could muster.</p>
<p class="western5">The view from the chopper proved to be no
distraction. Out of the window the corpse of one more unimportant
city lay before him, ragged and dirtied by violence, neglect and
nature flaking away the remnants of civilization. They were cities
populated by the walking dead—the men, women and children who had
found no rest in their demise. The wretched creatures were tortured
by a malodorous and corrupt immortality that robbed them of their
vital spark and imposed a ravenous hunger. They were
forever-shambling husks in a perpetual hunt for living flesh.</p>
<p class="western5">Cahz opened his eyes, desperate to lose the
nagging dread of coming in country. He purposefully kept his gaze
inside the cabin and focused his attention on the minutia.</p>
<p class="western5">To his right was an invisible demarcation
line—a few extra inches of space he would not cross. That area
belonged to Idris, the pilot. Cahz didn’t like to engage him in
conversation or intrude on his side of the cabin. It wasn’t a
festering hostility that made Cahz standoffish. It was fear. Not
fear of the man; Idris was a nice enough guy, a bit reserved and
quiet, but there was nothing menacing about him. Cahz was fearful
of something going wrong.</p>
<p class="western5">It was the fear that he might initiate some
distraction that would cause them to crash, were he to nudge Idris’
elbow at the vital point in a manoeuvre or break his concentration
at a crucial moment. It wasn’t that Cahz feared dying. After all,
these days there were worse things than death.</p>
<p class="western5">He looked around the cabin trying to distract
himself from the discomfort and noise.</p>
<p class="western5">The chopper they flew in hadn’t been designed
to ferry troops but it served well enough in its current roll. She
was small, not so much compact as cramped, but she had good fuel
economy and an excellent range. All things considered not a bad run
about. Cahz very much doubted any new helicopters had come off any
production lines for some years now. This was the third chopper
sequestered to them in as many years. The first broke down and was
dismantled for parts; he assumed because the vital part that was
broken no longer had a replacement. The second chopper no one knew
what happened to it or its crew. It flew out one bright morning on
a specimen run and never returned. No distress call, no wreckage,
nothing. She just vanished.</p>
<p class="western5"><i class="calibre3">Maybe it’s still down there in that grey
landscape</i>, Cahz thought. The land, for all its greenery, looked
washed out and dreary. It was a depressing sight.</p>
<p class="western5">Thirty minutes ago he’d been briefly occupied
by the sunrise—a palette of vivid golds, azure blues and iridescent
pinks as the lingering period of murky twilight was forced back. It
was beautiful; a rare splash of colour to this dead world.</p>
<p class="western5">However, the brilliant orange glow had
quickly been obscured by a flock of dark rain laden clouds. With
that the world had taken on the depressing insipid hues that
shrouded the dead below. And as it did the claustrophobic
oppression had returned.</p>
<p class="western5">Cahz had been a soldier all his adult life.
He’d lost count of the number of flights over hostile
territory—flights far more dangerous than this. The enemy below
didn’t fire ground-to-air missiles, they didn’t drive a car packed
full of explosives into your base, and they didn’t leave
indiscriminate booby traps. He’d served much of his time in uniform
fighting insurgents, being shot at and bombed. But this world was a
long way from those tours. In the old world people had tried to
kill him, people had tried to help him and some people couldn’t
have cared less about him. But there were always people. The
villages or towns or cities may have been battle damaged, but
without fail life would return. The traders would reopen the
market, the housewife would brush out the dirt and shattered glass
and the kids would play in the street.</p>
<p class="western5">Not now. The city below was scarred from the
damage inflicted during the chaos of collapse. But the wounds to
the cityscape hadn’t been healed by man. Instead nature had started
the long task of breaking the concrete down and reclaiming the
land, soothing the harsh grey back to a verdant green.</p>
<p class="western5">Unable to escape the dismay overwhelming his
consciousness, Cahz purposefully switched back to his military
mindset, preoccupying himself with the practical aspects of his
mission.</p>
<p class="western5">He toggled his radio. “Angel, what’s your
situation?”</p>
<p class="western5">“Secure and in position,” Angel answered in
her thick Russian accent. “I have eyes on Bate and field of fire is
good.”</p>
<p class="western5">“Bates, how’s your position?” Cahz asked.</p>
<p class="western5">Bates’ voice crackled over the short range
radio, “A-okay. Area is clear. Capture net is set. Activity is high
but I’m ready when you are.”</p>
<p class="western5">Cahz re-examined the terrain below. Raggedy
figures shambled their way towards his man on the ground.</p>
<p class="western5">There were more than he’d expected. Normally
their quarry was thinly spread out from their aimless wanderings,
but here they looked more concentrated.</p>
<p class="western5"><i class="calibre3">Still, it might make things go
quicker</i>, Cahz thought as he cast his gaze out at the dead
city.</p>
<p class="western5">He craned round, trying to take in as much of
the terrain as possible when he caught the eye of the last member
of his team. Cannon was a giant of a man and had to cock his head
slightly to fit in the cabin. His broad muscular chest wouldn’t
have looked out of place on a power lifter. And now he was wedged
into the back of the tiny chopper.</p>
<p class="western5"><i class="calibre3">At least I get to ride shotgun</i>, Cahz
reminded himself.</p>
<p class="western5">Even though the other two team members were
on the ground, Cannon filled the back seats. His only relief was
the half hour or so when they set up and executed the collection.
As soon as Bates and Angel were back in the cabin Cannon spent his
time crushed up against the door.</p>
<p class="western5">The whole nine hours or so the round trip
took, Cannon and Idris wouldn’t leave their seats.</p>
<p class="western5">“Your biggest risk,” Bates had once joked,
“is getting deep vein thrombosis from sitting on your ass.”</p>
<p class="western5">Cannon had quipped back, “And your biggest
risk is me.”</p>
<p class="western5">“I don’t know why you insist on coming
along,” Cahz had commented long ago. “Let French or one of the
Marines come in country.”</p>
<p class="western5">Cannon had pouted ever so slightly in
disdain, “And who’d look after your ass?”</p>
<p class="western5">That was all the dissuasion there would be.
Cannon just sat there quietly waiting for his commander to take the
lead.</p>
<p class="western5">It always amazed Cahz that even in this
shattered world the logistics corps had produced body armour large
enough to fit his frame. It had proven impossible to supply one
small part for a helicopter and yet extra-extra large tactical
vests were common fare.</p>
<p class="western5">“How’s it look to you?” Cahz asked.</p>
<p class="western5">“Looks risky boss,” Cannon said, “but that’s
what they pay us for.”</p>
<p class="western5">Cahz gave a chortle. “When did you last get
paid? You got a Cayman Island account I don’t know about?”</p>
<p class="western5">Cannon gave a wry smile.</p>
<p class="western5">“Quicker we get this done the quicker we go
home,” Cahz said, turning back round. He shifted the carbine and
fidgeted to get back into a semi-comfortable position before
pulling his mission map from a pouch on his body armour.</p>
<p class="western5">He stared at the map. The paper was crisp and
new but it might as well be a century’s old piece of parchment. It
may have been printed only six or seven years ago but the land
below bore little resemblance. The sweeping roads were broken and
incomplete, the sprawling cities crumbling to rubble and the place
names lost to human awareness.</p>
<p class="western5">The city below looked anything but familiar
underneath the grime and feral vegetation—a terrain more hostile
and more deadly than any map could indicate.</p>
<p class="western5">After the loss of a chopper and its crew the
ship’s captain had argued for reducing the complement on specimen
runs. The captain had wanted to reduce risk exposure. In theory a
collection crew could consist of just two men. The pilot was a
given but the capture net only needed one man to set it. Maybe the
team size would have been cut if it hadn’t been for Cahz’s
arguments. The extra manpower was an insurance policy in case of
the unexpected. If anything did go wrong out here Cahz knew the
only chance for survival was to work as a team.</p>
<p class="western5">Like the map the mission plan was not the
reality. On paper things weren’t as messy.</p>
<p class="western5">Cahz toggled his radio again. “Okay people,
let’s make this clean and quick. Bates, call them in.”</p>
<p class="western5">He let his thumb slip off the send button and
looked out over the crumbling city.</p>
<p class="western5">Things were certainly a mess out here.</p>
<p class="western5"> <br class="calibre1"/></p>
<p class="c2">* * *</p>
<p class="western5"> <br class="calibre1"/></p>
<p class="western5">“Get up, Ali!”</p>
<p class="western5">Ali was wrenched out of his sleep. He
swallowed deep in his parched throat, trying to move the sticky
mucus from his mouth.</p>
<p class="western5">People were shouting and rushing around.
Doors slamming and lights blazing.</p>
<p class="western5">The warehouse was alive with the sounds of
panic.</p>
<p class="western5">Ali opened his eyes but the bright lights
made him blink and turn away.</p>
<p class="western5">More tentatively Ali peaked out from behind
his eyelids, hand against his brow fending off the worst of the
light. The hollering in the warehouse pressed in and he felt the
panic gash a hole through his grogginess.</p>
<p class="western5">The main lights were on and Ali knew that was
significant. The trickle of power the survivors got from their
rooftop solar cells wasn’t enough to feed the lights. Power must be
getting drawn from the batteries. He knew that electricity was
precious. To use it for the lights meant there’d be no power left
for the microwave oven or the hot plate or any of the other
atavistic appliances they relied upon.</p>
<p class="western5">He unzipped his sleeping bag and tussled with
its embrace to get his feet free. As he kicked it loose he glanced
around. His fellow inmates were bounding to their feet. They
shouted and clamoured but Ali couldn’t hear the tell-tale moans or
see any shambling figures.</p>
<p class="western5">“Where?!” Ali barked out, pulling himself
upright.</p>
<p class="western5">“On the roof!” Ryan, dressed in just his
underwear, called back as he disappeared towards the stairwell.</p>
<p class="western5">Ali was standing now, alert and solid.
Straight backed, adrenaline coursing through him, scanning the room
for danger. Ali was a big man but he wasn’t a youth like Ryan. His
body wasn’t as taut as the younger man’s but he was still powerful.
He knew that even after months of emaciation he could still put up
a fight against the ghouls.</p>
<p class="western5">Ali looked at the stair door as it swung shut
and mumbled to himself, “How could they be on the roof?”</p>
<p class="western5">If the zombies outside ever broke through
he’d anticipated one of the fire doors or the main entrance would
be the source. The roof didn’t make sense. How could zombies get up
there?</p>
<p class="western5">With a flicker of insight Ali surmised the
roof must be where his fellow survivors were retreating. Glancing
round, there was no immediate threat. He looked down at his bare
feet. He had been sleeping in a vest and threadbare underpants. He
knew his long black beard would be wild and unruly, his hair just
as untamed. He knew he looked a sight. Hastily, Ali pulled his
jeans on and slipped his feet into his shoes.</p>
<p class="western5">“Where are they coming from?” George asked no
one in particular as he bumbled through the door.</p>
<p class="western5">“Don’t know,” Elspeth admitted, scurrying
after him, cradling a baby in her arms.</p>
<p class="western5">The warehouse was strangely still. He was the
last of the group to leave for the roof and now he stood alone in
the threatening silence. As he pulled his thick and well worn shirt
on, Ali listened for the moans of the zombies drawing closer. All
he could hear were the footfalls in the stairwell and beyond
that...</p>
<p class="western5">Ail focused carefully and tuned in. There it
was like an unending incantation. The incessant droning of a
thousand coarse voices conveyed in the air as background noise.</p>
<p class="western5">The whole situation confused him. There were
no hordes of undead pressing into the warehouse, yet his companions
had run off to the roof.</p>
<p class="western5">Still no less confused, Ali left his bedside
and made his way up. He was perplexed by what was going on but no
longer fearful of imminent attack.</p>
<p class="western5">His head pounded. He wasn’t sure if it was
the startled wakening or the lack of coffee. His knees creaked
almost as loudly as the heavy fire door he barged open to access
the stairs. His big frame, greatly reduced by the starvation
rations, had taken its toll on his joints. There had been a pallet
of cod liver oil tablets in the warehouse and for years he’d taken
a capsule every day. One day he’d opened a tub to find the gelatine
had spoiled, leaving the contents to ooze to the bottom of the
container. That marked the end of his self-medication and his
joints had started aching ever the more.</p>
<p class="western5">With each step his joints eased off. It was
always hard for Ali to get going in the morning these days. He
would shuffle around much like the undead outside until his
ligaments and muscles had eased off.</p>
<p class="western5">Only in his mid-forties and Ali felt that
every morning he awoke he was on the steep slope to old age. The
cold metal handrail made his stiff fingers tingle unpleasantly. He
let out a shallow cough that echoed off the rough brick walls in
the empty stairwell.</p>
<p class="western5"><i class="calibre3">If I feel so bad just waking up</i>, he
thought, <i class="calibre3">how must those dead fucks outside feel?</i></p>
<p class="western5">Ali spoke to the empty walls as he hauled
himself onto the last step, “Maybe that’s why you moan.”</p>
<p class="western5">At the top of the stairs he swung the door
open, flooding the stairwell with natural light. It took a few
seconds to adjust to the brightness and when he did he saw the
whole group there.</p>
<p class="western5">Sarah, her light brown hair whipping in the
wind, stood shoulder to shoulder with Ryan at the edge of the roof.
Ryan looked cold. He stood there bare footed, wearing only his
flimsy boxer shorts. Slightly behind them was Nathan, arms folded
close to his chest and appearing as cold as Ryan despite wearing
jeans and a t-shirt. Behind them Ray stood with the elf-like eight
year old Jennifer, now the second youngest of the group since
Samantha’s baby had been born. At the back George and Elspeth stood
not far from the door.</p>
<p class="western5">Elspeth cradled her granddaughter in her
arms. Her dull grey hair and the weathered lines around her eyes
and lips seemed to disappear when she held the girl. She held her
tight to her chest the way she’d held the infant’s dying mother not
so long ago. No doubt these past few months had been the hardest
she’d faced since settling here, the newborn girl a constant link
with the daughter she had lost.</p>
<p class="western5">It struck Ali that the sweet infant in her
arms must stir a conflicting bundle of emotions for her—the joy of
a granddaughter and a reminder of the cost of her birth.</p>
<p class="western5">Elspeth’s daughter, Samantha, had hooked up
with Ryan. Ali had never used any romantic terms to define their
relationship. Ryan, Samantha, Sarah and Nathan were all a similar
age. It was inevitable than in such close proximity, with youthful
hormones brewing, that there would be a pairing. Nathan had hit on
Sarah, unsuccessfully as far as Ali could tell, but Ryan and
Samantha had come together. He had heard them in the echoing
warehouse even when they’d tried to conceal their trysts. The
warehouse was big but there were few private spots.</p>
<p class="western5">“What’s going on, George?” Ali asked the old
man next to him.</p>
<p class="western5">“Sarah’s seen a helicopter.” George flashed a
smile that showed he’d taken the time to put in his false
teeth.</p>
<p class="western5">“There’s music playing,” Elspeth helpfully
added.</p>
<p class="western5">Ali was taken aback. “A helicopter?”</p>
<p class="western5">He cocked his head slightly and listened. He
took a few paces further onto the roof and stopped by the water
butts. His long black beard bobbed in the gusts of wind. With his
thick hands he stroked the hair tame. As he stood there holding the
point of his beard he picked up the music drifting on the wind.</p>
<p class="western5">The army of corpses below also heard it. From
their besieging positions around the warehouse, some of the undead
had started wandering towards the noise.</p>
<p class="western5">Nathan stood slightly hunched against the
cold. He asked, “So what do we do? Light a signal fire or
something?”</p>
<p class="western5">“They might think it was just an accidental
fire,” Ryan pointed out.</p>
<p class="western5">Nathan turned round, running his gaze over
the rest of the roof. “Maybe we could use the solar panels like
signal mirrors?”</p>
<p class="western5">“They’re not looking for us—they’re not
looking for <i class="calibre3">anyone</i>,” Sarah said, her voice carrying a note
of dismay. “They’re not expecting anyone left alive.”</p>
<p class="western5">“So what are they here for?” Ray asked,
speaking up for the first time.</p>
<p class="western5">Ray was a logical man. Happier working out
problems on paper than engaging in the practicalities. Ali
remembered when they’d first arrived here Ray had spent the best
part of a week cataloguing the pallets of supplies within.</p>
<p class="western5">With his index finger Ray shoved his glasses
further up his nose, as much a habit as a necessity. “If they’re
scavengers they’re shit out of luck,” he said. “There’s nothing
left. We’ve picked this place clean.”</p>
<p class="western5">“Wasn’t much to pick,” Nathan complained.</p>
<p class="western5">Ryan shrugged his heavyset shoulders. “So how
do we signal them?”</p>
<p class="western5">Sarah was stolid. “We don’t. We go to
them.”</p>
<p class="western5">“<i class="calibre3">Go to them</i>, girl?” George’s overly
loud interjection startled Ali. The power behind that old voice was
stronger than Ali had heard in some time. George shook his head,
nodding in the direction of the music. “We don’t even know who they
are.”</p>
<p class="western5">“He’s got a point,” Ryan agreed. “They could
be worse than those things.”</p>
<p class="western5">“They might shoot us as soon as help us,”
Elspeth added.</p>
<p class="western5">Sarah turned her attention away from the
ruined city to look at her comrades. “It’s been years since we’ve
seen any marauders and none of them were in helicopters. Anyway,
they’re advertising their presence with the music. It’s like they
want to cause a commotion to shake the place up.” Sarah paused and
pressed her tongue to her bottom lip in contemplation. Finally she
said, “No. This has been our only chance of escape in years and it
may be our last.”</p>
<p class="western5">“Sarah, think about it,” George said, taking
a half step forward, “We’re safe in here.” With an arm carpeted in
grey hair he gestured back at the warehouse door. “The moment we
open the shutters there’ll be no turning back. They’ll be in here
and there won’t be no way of stoppin’ ‘em.”</p>
<p class="western5">Sarah shook her head slightly. “How much
longer will we be safe in here?”</p>
<p class="western5">No one answered. No one wanted to confront
the truth.</p>
<p class="western5">Ali wasn’t the smartest of men but he’d
picked up on whispered conversations over the past couple of
months. He’d watched as the gaunt bespectacled man had scurried
around the warehouse with his clipboard in hand.</p>
<p class="western5">“Tell them straight, Ray,” Sarah demanded,
breaking the silence.</p>
<p class="western5">Ray muttered into his chest like a chastised
school boy, “I don’t know.”</p>
<p class="western5">“Ray!” Sarah barked.</p>
<p class="western5">“Maybe four or five weeks worth of food,” Ray
admitted, “and that’s rationing out even thinner than now.”</p>
<p class="western5">Elspeth’s face dropped, shocked by the
revelation.</p>
<p class="western5">The predicament wasn’t a shock to Ali. Like
most of the survivors he had been complicit in his ignorance,
preferring to ignore the inevitable for as long as possible. Ali
had pushed aside the thoughts of how perilous their existence was.
He knew the food would run out but he knew there was nothing they
could do about it. Each winter he had foraged among the decaying
buildings trying to find supplies of food. As the years dragged on
they had to forage further afield. The incident with the cod liver
oil capsules had brought things into sharp focus. Knowing their
plight last winter he’d gone out further than ever before. He and
the younger ones had fashioned a sledge and set up a series of
waypoints. It was the longest any of them had spent away from the
warehouse since they’d arrived. Day after day they picked through
the frozen dead and frost shattered rubble in a vain attempt to
bolster their dwindling supplies. But the town had already been
gutted.</p>
<p class="western5">When the first thaws of spring released the
dead from their icy prisons it was time to retreat. And as the snow
of winter had melted away, a steady trickle of undead had found
them, like migrating birds finding the same nesting site year after
year. In previous years even the most concerted winter cull had
proven futile. Come spring the undead would return. Slowly at
first, a handful at a time, but before long there were thousands
crushed up against the fences. Summer was still at least a month
away but already the warehouse was swamped.</p>
<p class="western5">Ali tuned back in to the conversation to hear
Nathan complain, “Ryan’s guzzled the last of the Jack.”</p>
<p class="western5">“Nathan!” Elspeth chided from next to
him.</p>
<p class="western5">Ryan faced the group from the lip of the roof
and asked, “What do we do?”</p>
<p class="western5">He stood there, his skin numbed from the
cold, casting his gaze across everyone. Ryan was hoping that
someone could come up with a better plan, but as he looked at his
emaciated friends no one did.</p>
<p class="western5">“Fuck it,” Nathan said, breaking the
awkwardness. “Sarah’s right. We have to go to them.”</p>
<p class="western5">Ali stroked his rowdy beard flat and took a
deep swallow to lubricate his voice and said against the wind,
“Hold on.”</p>
<p class="western5">It seemed that even the baby in Elspeth’s
arms went quiet as everyone turned to look at him.</p>
<p class="western5">“You’re seriously suggesting we go out
there?” Ali looked over the heads of his audience into the distance
where the chopper had been spotted.</p>
<p class="western5">Sarah’s voice was acidic, “What else would
you suggest?”</p>
<p class="western5">Sarah had been quite cold towards him in the
early days. Ali was used to it; he’d always been an outsider and
maybe that suited him. Now looking out at the people he’d been
thrown in with—<i class="calibre3">incarcerated with</i>—people he would never have
chosen to be with, he realised these were the closest friends he’d
ever had. Always quiet, always reserved, Ali had kept himself to
himself but in the close confinement of their sanctuary the time
had worn down many barriers. After those first fearful months
they’d started to grow together. Week on week, month on month, year
on year the barriers had melted away. Every one of these people
were his friends and Ali knew they would all die if they didn’t
make the right decision.</p>
<p class="western5">He asked the crowd, “You’re thinking things
are so bad that it justifies going out there?”</p>
<p class="western5">Sarah caught Ray’s eye before speaking. “They
will be in a month.”</p>
<p class="western5">Ali could see what Sarah was trying to do.
She was trying to force the group to the same conclusion she had.
But he knew that the older people, George and Elspeth, wouldn’t
budge if they felt pressured.</p>
<p class="western5">“There are thousands of those pus bags
between here and there. One bite, one scratch and that’s all it
takes to turn you.” Looking around, Ali checked that the gravity of
what he had just said had sunk in. He continued highlighting the
unknowns to his companions. “You plan on dodging those things long
enough to get to a helicopter full of people who are mystery to
you?”</p>
<p class="western5">Ray shuffled nervously but other than that
the group was silent.</p>
<p class="western5">“As Elspeth said, they may not be friendly,
they may want to shoot us, they may refuse to take us. What then?”
Ali paused for a response.</p>
<p class="western5">“We don’t have time to argue this,” Ryan
said. “Who knows how long they’ll be there.”</p>
<p class="western5">“Is it truly worth the risk?” Ali asked. “Do
you want to wait here and starve to death or take the chance?”</p>
<p class="western5">The congregation on the rooftop started
looking at each other. Ali could see people were starting to
seriously weigh up the two options, the slow death through
starvation or the risk of trying to get help.</p>
<p class="western5">“I only say that because everyone has to be
sure what choice there is.”</p>
<p class="western5">Ali leant back against a water tank and
waited for the group to decide. He already knew the only option.
Starving to death would drive everyone mad. They’d end up fighting
each other for crumbs. He remembered his grandmother telling him of
a famine when she was a girl. He remembered her arthritis gnarled
fingers jabbing out at him as she told of how she’d eaten the rats
that were gnawing on her brother’s corpse. Ali never knew how true
her stories were, but the fear he had as a boy sitting on the floor
looking up at the ancient matriarch was with him again.</p>
<p class="western5">Even if their bid for safety failed at least
they would die trying. And that was one thing his grandmother had
instilled in him: keep fighting because in the end all you have is
what you fight for.</p>
<p class="western5">Slowly the group started to look more solemn
as one by one they came round to Sarah’s side.</p>
<p class="western5">Sensing the change in mood, Sarah said,
“Okay, leave everything. Only carry a weapon. It’s not far to the
square but there’s a lot of them and we’ll have to run the whole
way. Nathan, Ryan, get all the Molotov cocktails we have left.
Let’s try to thin them out.”</p>
<p class="western5">Ali smiled as he watched Sarah pull the group
together. She had a knack for taking charge, a natural leadership.
She was smart and pretty even with the trendy lip piercing.</p>
<p class="western5">Ali took a moment to survey his friends. Ryan
and Nathan were already rushing to the floor access to get the
petrol bombs. They were young men, strong with youth. Ryan was
physically bigger than all the rest of the survivors but Ali
worried more about him than anyone else. The past few months he’d
hardly spent a day sober as he’d tried to drown out the pain of his
loss. Elspeth, he knew, had suffered Samantha’s death more deeply
than the rest. The other survivors had lost a friend but Elspeth
had lost a daughter. But there, clutched in her arms, swaddled in a
cream shawl, was her reason to push past the pain.</p>
<p class="western5">Looking at their faces it was obvious that
Ray, George and Elspeth were lost, frozen by the enormity of the
group’s decision. Ali knew he would have to ease them into
action.</p>
<p class="western5">“Jennifer,” Ali called out to the small girl,
“let’s go downstairs and get dressed properly before we go
out.”</p>
<p class="western5">Stretching out an arm, Ali offered the eight
year old a hand. He guessed she was about eight, no one could be
sure. The orphan knew she was four when they’d found her but she
couldn’t say when her birthday was so her age was an ongoing
estimation.</p>
<p class="western5">“Come on,” Ali called to the rest of the
group. “Best we hurry.”</p>
<p class="western5"> <br class="calibre1"/></p>
<p class="western5"> <br class="calibre1"/></p>
</div>
</div>
</template>
<script>

export default {
  name: 'chapter',
  data() {
    return {}
    },
      components: {
    }
  }
</script>



// WEBPACK FOOTER //
// tmp_585a07e3e5e55219dccf95a383be276f_OzNgdZ.ch.fixed.fc.tidied.stylehacked.xfixed_split_003.vue?2e2b5a6b